<template>
    <div class="employee-history">
        <p class="employee-history__intro">Изменения карточки, назначения и кадровые события</p>
        <div v-for="group in groups" :key="group.key" class="employee-history__group">
            <button v-if="group.imported" type="button" class="employee-history__imports"
                    :aria-expanded="importsOpen ? 'true' : 'false'" @click="importsOpen = !importsOpen">
                <i class="fas fa-database" aria-hidden="true"></i>
                <span>Перенос и сверка данных <span class="employee-history__count">{{ group.items.length }}</span>
                    <small>Записи о загрузке данных из прежних систем</small>
                </span>
                <i :class="['fas', importsOpen ? 'fa-chevron-up' : 'fa-chevron-down']" aria-hidden="true"></i>
            </button>
            <div v-if="!group.imported || importsOpen" class="employee-history__timeline">
                <article v-for="item in group.items" :key="item.id" class="employee-history__event">
                    <span :class="['employee-history__icon', 'employee-history__icon--' + item.category.tone]" aria-hidden="true">
                        <i :class="['fas', item.category.icon]"></i>
                    </span>
                    <div class="employee-history__body">
                        <div class="employee-history__heading">
                            <h3>{{ item.title }}</h3>
                            <time :datetime="item.timestamp">{{ formatDate(item.timestamp) }}</time>
                        </div>
                        <div class="employee-history__meta">
                            <span>{{ item.category.label }}</span><span aria-hidden="true">·</span>
                            <EmployeeLink v-if="item.actorId" :user-id="item.actorId" :fallback="item.actorName || 'Автор не указан'" compact />
                            <span v-else>{{ item.actorName || 'Система' }}</span>
                            <template v-if="item.source && item.source !== item.category.label && item.source !== 'Система'">
                                <span aria-hidden="true">·</span><span>{{ item.source }}</span>
                            </template>
                        </div>
                        <blockquote v-if="item.comment" class="user-quote">{{ item.comment }}</blockquote>
                        <p v-if="item.description" class="employee-history__description">{{ item.description }}</p>
                        <b-button v-for="id in item.financialAuditIds || []" :key="'finance-' + id" variant="link" size="sm" @click="financialAuditId = id">
                            Финансовый журнал №{{ id }} · требуется доступ к ЗП
                        </b-button>
                        <dl v-if="item.primary.length" class="employee-history__facts">
                            <div v-for="row in item.primary" :key="row.key" class="employee-history__fact">
                                <dt>{{ row.label }}</dt>
                                <dd v-if="row.before !== undefined" class="employee-history__comparison">
                                    <span><small>Было</small>{{ row.before }}</span>
                                    <span><small>Стало</small>{{ row.after }}</span>
                                </dd>
                                <dd v-else>{{ row.value }}</dd>
                            </div>
                        </dl>
                        <router-link v-if="item.requestId" :to="{name: 'request-card', params: {id: String(item.requestId)}}" class="employee-history__request">
                            Заявка № {{ item.requestNumber || item.requestId }}
                        </router-link>
                        <details v-if="item.details.length" class="employee-history__details">
                            <summary>Подробности <span class="employee-history__count">{{ item.details.length }}</span></summary>
                            <dl class="employee-history__facts">
                                <div v-for="row in item.details" :key="row.key" class="employee-history__fact">
                                    <dt>{{ row.label }}</dt>
                                    <dd v-if="row.before !== undefined" class="employee-history__comparison">
                                        <span><small>Было</small>{{ row.before }}</span>
                                        <span><small>Стало</small>{{ row.after }}</span>
                                    </dd>
                                    <dd v-else>{{ row.value }}</dd>
                                </div>
                            </dl>
                        </details>
                    </div>
                </article>
            </div>
        </div>
        <EmployeeMergeFinancialAudit v-if="financialAuditId" :event-id="financialAuditId" @close="financialAuditId = null" />
    </div>
</template>

<script>
import EmployeeLink from '@/components/EmployeeLink.vue';
import EmployeeMergeFinancialAudit from './EmployeeMergeFinancialAudit.vue';
export default {
    name: 'EmployeeHistoryList',
    components: {EmployeeLink, EmployeeMergeFinancialAudit},
    props: {items: {type: Array, default: () => []}},
    data: () => ({importsOpen: false, financialAuditId: null}),
    computed: {
        groups() {
            return [
                {key: 'events', items: this.items.filter(item => !item.category.imported)},
                {key: 'imports', imported: true, items: this.items.filter(item => item.category.imported)}
            ].filter(group => group.items.length);
        }
    },
    methods: {
        formatDate(value) {
            if (!value) return 'Дата не указана';
            const date = new Date(value);
            return Number.isNaN(date.getTime()) ? 'Дата не указана' : date.toLocaleString('ru-RU', {
                day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
            });
        }
    }
};
</script>

<style scoped>
.employee-history { min-width: 0; color: #34495e; }
.employee-history__intro { margin: 0 0 20px; color: #65788b; font-size: 13px; }
.employee-history__event { display: grid; grid-template-columns: 34px minmax(0, 1fr); gap: 12px; position: relative; padding-bottom: 18px; }
.employee-history__event::before { content: ''; position: absolute; left: 16px; top: 38px; bottom: 4px; width: 1px; background: #e0e9ef; }
.employee-history__event:last-child::before { display: none; }
.employee-history__icon { display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 11px; font-size: 13px; }
.employee-history__icon--blue { color: #356b98; background: #edf5fc; }
.employee-history__icon--mint { color: #347568; background: #edf8f3; }
.employee-history__icon--sand { color: #85673a; background: #faf5eb; }
.employee-history__icon--muted { color: #63758a; background: #f0f3f7; }
.employee-history__body { min-width: 0; border: 1px solid var(--argus-glass-border, #e2eaf0); background: var(--argus-glass-solid, #fcfdfe); border-radius: 12px; padding: 13px 16px; box-shadow: inset 0 1px 0 #fff, 0 2px 6px rgba(45, 70, 90, .025); overflow-wrap: anywhere; }
.employee-history__heading { display: flex; align-items: baseline; justify-content: space-between; gap: 8px 18px; flex-wrap: wrap; }
.employee-history__heading h3 { font-size: 15px; font-weight: 650; line-height: 1.4; margin: 0; color: #2f465e; }
.employee-history__heading time { color: #6c7c8c; font-size: 12px; }
.employee-history__meta { display: flex; align-items: baseline; flex-wrap: wrap; gap: 3px 7px; color: #66798a; font-size: 12px; margin-top: 5px; }
.employee-history__meta ::v-deep .employee-link-wrap { min-width: 0; max-width: 100%; }
.employee-history__meta ::v-deep .employee-link { white-space: normal; text-align: left; font-size: inherit; }
.employee-history__meta ::v-deep .employee-link__name { white-space: normal; overflow: visible; text-overflow: clip; }
.employee-history__description { margin: 11px 0 0; font-size: 14px; line-height: 1.6; white-space: pre-line; }
.employee-history__facts { margin: 10px 0 0; font-size: 13px; }
.employee-history__fact { display: grid; grid-template-columns: minmax(120px, 190px) minmax(0, 1fr); gap: 6px 16px; padding: 7px 0; }
.employee-history__fact + .employee-history__fact { border-top: 1px solid #e8eef3; }
.employee-history__fact dt { font-weight: 400; color: #637589; }
.employee-history__fact dd { margin: 0; white-space: pre-line; }
.employee-history__comparison { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.employee-history__comparison small { display: block; color: #6c7c8c; font-size: 11px; margin-bottom: 3px; }
.employee-history__details { margin-top: 10px; }
.employee-history__details summary { width: fit-content; max-width: 100%; padding: 5px 0; cursor: pointer; color: #356c98; font-size: 13px; }
.employee-history__details summary:focus-visible, .employee-history__imports:focus-visible { outline: 2px solid #347db4; outline-offset: 3px; border-radius: 4px; }
.employee-history__count { display: inline-block; font-size: 11px; color: #64798d; background: #edf2f7; border-radius: 10px; padding: 1px 6px; margin-left: 5px; }
.employee-history__request { display: inline-block; margin-top: 10px; font-size: 13px; }
.employee-history__imports { width: 100%; display: flex; gap: 12px; align-items: center; text-align: left; border: 1px solid #dfe8ef; border-radius: 12px; padding: 13px 16px; color: #4d647a; background: #f7fafc; font-size: 14px; margin-bottom: 16px; }
.employee-history__imports > span { flex: 1; min-width: 0; }
.employee-history__imports small { display: block; font-size: 12px; margin-top: 3px; color: #6c7c8c; }
@media (max-width: 600px) {
    .employee-history__event { grid-template-columns: 24px minmax(0, 1fr); gap: 7px; }
    .employee-history__icon { width: 24px; height: 28px; border-radius: 8px; font-size: 11px; }
    .employee-history__event::before { left: 11px; top: 32px; }
    .employee-history__body { padding: 10px; }
    .employee-history__heading { display: block; }
    .employee-history__heading h3 { font-size: 14px; }
    .employee-history__heading time { display: block; margin-top: 4px; }
    .employee-history__fact, .employee-history__comparison { grid-template-columns: minmax(0, 1fr); gap: 4px; }
    .employee-history__comparison { gap: 9px; }
}
@media print {
    .employee-history__body { box-shadow: none; break-inside: avoid; }
}
</style>

<style src="@/styles/user-quote.css"></style>
